import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Row, Col, Card, Button, Input, Tabs, message, Slider, Switch, Badge, Tooltip } from 'antd';
import {
    PlayCircleOutlined,
    StopOutlined,
    AudioOutlined,
    SendOutlined,
    SoundOutlined,
    VideoCameraAddOutlined,
    VideoCameraOutlined,
    UserOutlined,
    ClockCircleOutlined,
    DisconnectOutlined
} from '@ant-design/icons';
import styles from './index.module.scss';
import type { SpeechRecognition, WindowWithSpeech } from '../../types/employee';
import { sendMessage, createOffer, closeTalkSession, getSessionStatus } from '../../api/employee';
import { useLocation, useParams } from 'react-router-dom';

const { TabPane } = Tabs;
const { TextArea } = Input;

interface SessionStats {
    total_sessions: number;
    max_sessions: number;
    active_users: number;
}

interface ChatMessage {
    sender: 'user' | 'system' | 'digital_human';
    text: string;
    timestamp?: number;
}

// 生成用户ID的函数
const generateUserId = (): string => {
    const stored = localStorage.getItem('live_user_id');
    if (stored) return stored;

    const newId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('live_user_id', newId);
    return newId;
};

const useTalking = () => {
    const [status, setStatus] = useState<'disconnected' | 'connecting' | 'connected'>('disconnected');
    const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
    const [sessionId, setSessionId] = useState<string | null>(null);
    // const [userId] = useState<string>(() => generateUserId());
    const [lastActivity, setLastActivity] = useState<number>(Date.now());
    const [connectionTime, setConnectionTime] = useState<number | null>(null);

    const pcRef = useRef<RTCPeerConnection | null>(null);
    const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);

    // 更新活动时间
    const updateActivity = useCallback(() => {
        setLastActivity(Date.now());
    }, []);

    // 心跳检测
    const startHeartbeat = useCallback(() => {
        if (heartbeatIntervalRef.current) {
            clearInterval(heartbeatIntervalRef.current);
        }

        heartbeatIntervalRef.current = setInterval(() => {
            const now = Date.now();
            const timeSinceActivity = now - lastActivity;

            // 如果9分钟没有活动，显示警告
            if (timeSinceActivity > 9 * 60 * 1000) {
                message.warning('即将因无活动而断开连接，请发送消息保持连接');
            }
        }, 60000);
    }, [lastActivity]);

    // 修复：先定义 stop，解决 start 中的依赖问题
    const stop = useCallback(() => {
        if (pcRef.current) {
            pcRef.current.close();
            pcRef.current = null;
        }
        setMediaStream(null);
        setStatus('disconnected');
        setSessionId(null);
        setConnectionTime(null);

        // 清理定时器
        if (heartbeatIntervalRef.current) {
            clearInterval(heartbeatIntervalRef.current);
            heartbeatIntervalRef.current = null;
        }

        message.destroy('connecting');
        // message.info('连接已断开');
        console.log("Connection stopped.");
    }, []);

    const negotiate = useCallback(async (pc: RTCPeerConnection) => {
        try {
            pc.addTransceiver('audio', { direction: 'recvonly' });
            pc.addTransceiver('video', { direction: 'recvonly' });

            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);

            // 等待 ICE gathering 完成
            await new Promise<void>(resolve => {
                if (pc.iceGatheringState === 'complete') {
                    resolve();
                } else {
                    const checkState = () => {
                        if (pc.iceGatheringState === 'complete') {
                            pc.removeEventListener('icegatheringstatechange', checkState);
                            resolve();
                        }
                    };
                    pc.addEventListener('icegatheringstatechange', checkState);
                }
            });

            const response = await createOffer({
                sdp: pc.localDescription!.sdp,
                type: pc.localDescription!.type
            });

            const answer = response.data.data;
            console.log('Got answer:', answer);

            setSessionId(answer.sessionid);
            await pc.setRemoteDescription(
                new RTCSessionDescription({
                    type: 'answer',
                    sdp: answer.sdp
                })
            );


            setStatus('connected');
            setConnectionTime(Date.now());
            updateActivity();
            startHeartbeat();
            message.success('连接成功');

            // 如果后端返回了用户被替换的信息
            if (answer.user_replaced) {
                message.info('检测到重复登录，已关闭其他页面的连接');
            }

        } catch (error: any) {
            console.error("Negotiation failed:", error);
            setStatus('disconnected');
            message.error(`连接失败: ${error.message || '未知错误'}`);
        }
    }, [updateActivity, startHeartbeat]);

    const start = useCallback((useStun = false) => {
        if (pcRef.current) {
            console.warn("Connection already exists.");
            return;
        }

        setStatus('connecting');
        message.loading({ content: '正在连接...', key: 'connecting' });

        // 修复：移除 sdpSemantics 属性，因为它不是标准 RTCConfiguration 属性
        const config: RTCConfiguration = {};
        if (useStun) {
            config.iceServers = [{ urls: ['stun:stun.l.google.com:19302'] }];
        }

        const pc = new RTCPeerConnection(config);
        pcRef.current = pc;

        pc.addEventListener('track', (evt: RTCTrackEvent) => {
            console.log('Track received:', evt.track.kind);
            setMediaStream(evt.streams[0]);
            message.destroy('connecting');
        });

        pc.addEventListener('connectionstatechange', () => {
            const state = pc.connectionState;
            console.log('Connection state:', state);
            if (state === 'disconnected' || state === 'closed' || state === 'failed') {
                stop();
            }
        });

        negotiate(pc);
    }, [negotiate, stop]);


    // 组件卸载时清理
    useEffect(() => {
        return () => {
            if (heartbeatIntervalRef.current) {
                clearInterval(heartbeatIntervalRef.current);
            }
        };
    }, []);

    return {
        status,
        mediaStream,
        sessionId,
        lastActivity,
        connectionTime,
        start,
        stop,
        updateActivity
    };
};

const useSpeechRecognition = () => {
    const [isListening, setIsListening] = useState(false);
    const [transcript, setTranscript] = useState('');
    // 修复: 使用 WebSpeechRecognition 类型，以避免编译找不到类型
    const recognitionRef = useRef<SpeechRecognition | null>(null);

    useEffect(() => {
        const win = window as WindowWithSpeech;
        const SpeechRecognition = win.SpeechRecognition || win.webkitSpeechRecognition;
        if (SpeechRecognition) {
            // 修复: 实例化时使用构造函数
            recognitionRef.current = new SpeechRecognition();
            recognitionRef.current.continuous = true;
            recognitionRef.current.interimResults = true;
            recognitionRef.current.lang = 'zh-CN';

            // 修复: event 参数使用 any 避免环境类型缺失
            recognitionRef.current.onresult = (event: any) => {
                let interimTranscript = '';
                let finalTranscript = '';

                for (let i = event.resultIndex; i < event.results.length; ++i) {
                    if (event.results[i].isFinal) {
                        finalTranscript += event.results[i][0].transcript;
                    } else {
                        interimTranscript += event.results[i][0].transcript;
                    }
                }

                setTranscript(finalTranscript || interimTranscript);
            };

            // 修复: event 参数使用 any 避免环境类型缺失
            recognitionRef.current.onerror = (event: any) => {
                console.error('语音识别错误:', event.error);
                setIsListening(false);
            };

            recognitionRef.current.onend = () => {
                setIsListening(false);
            };
        }

        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.stop();
            }
        };
    }, []);

    const startListening = useCallback(() => {
        if (recognitionRef.current && !isListening) {
            setTranscript('');
            recognitionRef.current.start();
            setIsListening(true);
        }
    }, [isListening]);

    const stopListening = useCallback(() => {
        if (recognitionRef.current && isListening) {
            recognitionRef.current.stop();
            setIsListening(false);
        }
    }, [isListening]);

    return { isListening, transcript, startListening, stopListening };
};

const useSessionStats = () => {
    const [stats, setStats] = useState<SessionStats | null>(null);

    const fetchStats = useCallback(async () => {
        try {
            // 修复：使用导入的 getSessionStatus
            const response = await getSessionStatus({});
            // console.log('response- -- -->', response);

            // 修复：确保处理 API 响应结构
            if (response.data.status === 200) {
                setStats(response.data.data as SessionStats);
            } else {
                console.error('获取会话统计失败:', response.data.msg);
            }
        } catch (error: any) {
            console.error('获取会话统计失败:', error.message);
        }
    }, []);

    return { stats, fetchStats };
};

const ProfessionChat: React.FC = () => {
    // WebRTC状态管理
    const {
        status,
        mediaStream,
        sessionId,
        lastActivity,
        connectionTime,
        start,
        stop,
        updateActivity
    } = useTalking();

    const [messages, setMessages] = useState<ChatMessage[]>([
        { sender: 'system', text: '欢迎使用数字员工，请点击"开始连接"按钮开始对话。' }
    ]);
    const [chatInput, setChatInput] = useState('');
    const [ttsInput, setTtsInput] = useState('');
    const [videoSize, setVideoSize] = useState(100);
    const [isRecording, setIsRecording] = useState(false);
    const [useStun, setUseStun] = useState(false);
    const [showStats, setShowStats] = useState(false);

    const { isListening, transcript, startListening, stopListening } = useSpeechRecognition();
    const { stats, fetchStats } = useSessionStats();

    const [disconnectLoading, setDisconnectLoading] = useState<boolean>(false)

    // DOM引用
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const statsIntervalRef = useRef<NodeJS.Timeout | null>(null);

    const { profession: professionFromUrl } = useParams();

    
    // 将媒体流连接到video元素
    useEffect(() => {
        if (videoRef.current && mediaStream) {
            videoRef.current.srcObject = mediaStream;
            videoRef.current.muted = false;
            // 修复：使用 Promise.resolve 确保 await
            videoRef.current.play().catch(e => console.error("视频播放失败", e));
        }
    }, [mediaStream]);

    // 更新语音识别结果到输入框
    useEffect(() => {
        if (transcript) {
            setChatInput(transcript);
        }
    }, [transcript]);

    // 修复：sender 参数类型
    const addMessage = (text: string, sender: ChatMessage['sender'] = 'user') => {
        setMessages(prev => [...prev, { sender, text, timestamp: Date.now() }]);
    };

    const handleStart = () => {
        start(useStun);
    };

    const handleStop = async () => {
        if (!sessionId) {
            stop();
            return
        }

        try {
            setDisconnectLoading(true);
            // 先通知后端
            await closeTalkSession();
            message.success('连接已断开');
        } catch (error) {
            message.warning('后端断开失败,仅清理本地连接');
        } finally {
            // 无论后端是否成功,都清理前端
            stop();
            setDisconnectLoading(false);
        }


    };

    const handleSendChatMessage = async () => {
        console.log("professionFromUrl: ", professionFromUrl)
        const text = chatInput.trim();
        if (!text) return;

        addMessage(text, 'user');

        try {
            const response = await sendMessage({
                text: text,
                type: 'chat',
                sessionid: sessionId,
                interrupt: true,
                profession: professionFromUrl
            });

            console.log("response chat", response);
            if (response.data.status !== 200) {
                throw new Error(response.data.msg || '发送失败');
            }
        } catch (error: any) {
            message.error(`操作失败: ${error.message || '未知错误'}`);
        }
        setChatInput('');
        updateActivity(); // 更新活动时间
    };

    const handleSendTTSMessage = async () => {
        const text = ttsInput.trim();
        if (!text) return;

        addMessage(`已发送朗读请求: "${text}"`, 'system');

        // 修复： postHuman 未导入，假设其与 sendMessage 结构类似，用于 echo
        // 请根据你的实际 API 替换 postHuman 为正确的导入函数
        try {
            const response = await sendMessage({
                text: text,
                type: 'echo',
                sessionid: sessionId,
                interrupt: true
            });

            if (response.data.status !== 200) {
                throw new Error(response.data.msg || '发送失败');
            }
        } catch (error: any) {
            message.error(`朗读请求失败: ${error.message || '未知错误'}`);
        }

        setTtsInput('');
        updateActivity(); // 更新活动时间
    };

    // 按住说话功能
    // 修复：事件参数类型
    const handleVoiceStart = (e: React.MouseEvent | React.TouchEvent) => {
        e.preventDefault(); // 防止移动端默认行为
        if (status !== 'connected') return;
        startListening();
        updateActivity();
    };

    // 修复：事件参数类型
    const handleVoiceEnd = (e: React.MouseEvent | React.TouchEvent) => {
        e.preventDefault();
        stopListening();
        // 延迟发送，确保语音识别完成
        setTimeout(() => {
            if (transcript.trim()) {
                handleSendChatMessage();
            }
        }, 500);
    };

    // 强制关闭当前用户的所有会话
    const handleForceDisconnect = async () => {

        try {
            // 修复：使用导入的 closeTalkSession
            setDisconnectLoading(true)
            const response = await closeTalkSession();

            if (response.status === 200) { // 修复：根据 API 响应结构调整
                message.success('已强制断开所有连接');
                stop();

            } else {

                throw new Error(response.data.msg || '操作失败');
            }
            setDisconnectLoading(false)
        } catch (error: any) {
            setDisconnectLoading(false)
            message.error(`断开连接失败: ${error.message || '未知错误'}`);
        }
    };

    // 格式化连接时长
    const getConnectionDuration = useMemo(() => {
        if (!connectionTime) return '';
        const duration = Math.floor((Date.now() - connectionTime) / 1000);
        const minutes = Math.floor(duration / 60);
        const seconds = duration % 60;
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }, [connectionTime, status]);

    // 格式化空闲时间
    const getIdleTime = useMemo(() => {
        if (!lastActivity) return '';
        const idle = Math.floor((Date.now() - lastActivity) / 1000);
        if (idle < 60) return `${idle}秒前`;
        const minutes = Math.floor(idle / 60);
        if (minutes < 60) return `${minutes}分钟前`;
        const hours = Math.floor(minutes / 60);
        return `${hours}小时前`;
    }, [lastActivity]);


    return (
        <div className={styles.section}>

            <div className={styles.sectionHeader}>
                <div className={styles.title}>数字员工</div>
            </div>
            <Row gutter={24}>
                {/* 视频区域 */}
                <Col lg={14} xs={24}>
                    <Card
                        title={
                            <div className={styles.videoCardHeader}>
                                <div className={styles.statusInfo}>
                                    <Badge
                                        status={status === 'connected' ? 'success' : status === 'connecting' ? 'processing' : 'error'}
                                        text={
                                            status === 'connected' ? '已连接' :
                                                status === 'connecting' ? '连接中...' : '未连接'
                                        }
                                    />
                                    {connectionTime && (
                                        <span className={styles.connectionDuration}>
                                            <ClockCircleOutlined /> {getConnectionDuration}
                                        </span>
                                    )}
                                </div>
                                
                            </div>
                        }
                        className={styles.videoCard}
                        extra={
                            sessionId && (
                                <div className={styles.sessionInfo}>
                                    超过5分钟未通话将会自动断开
                                </div>
                            )
                        }
                    >


                        <div className={styles.videoWrapper}>
                            <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                // 修复：使用内联 style 传递动态值
                                style={{
                                    width: `${videoSize}%`,
                                }}
                                className={styles.videoElement}
                            />


                            {/* 连接超时警告 */}
                            {status === 'connected' && lastActivity &&
                                (Date.now() - lastActivity) > 8 * 60 * 1000 && (
                                    <div className={styles.timeoutWarning}>
                                        <div className={styles.warningTitle}>连接即将超时</div>
                                        <div className={styles.warningText}>
                                            请发送消息以保持连接
                                        </div>
                                    </div>
                                )}
                        </div>

                        <div className={styles.controls}>


                            <div className={styles.videoSizeControl}>
                                <label>视频大小调节: {videoSize}%</label>
                                <Slider
                                    min={50}
                                    max={100}
                                    value={videoSize}
                                    // 修复：Slider onChange 类型，确保接受 number
                                    onChange={(value: number | number[]) => setVideoSize(Array.isArray(value) ? value[0] : value)}
                                    className={styles.slider}
                                />
                            </div>
                            {status !== 'connected' ? (
                                <Button
                                    type="primary"
                                    icon={<PlayCircleOutlined />}
                                    onClick={handleStart}
                                    loading={status === 'connecting'}

                                >
                                    开始连接
                                </Button>
                            ) : (
                                <Button
                                    danger
                                    icon={<StopOutlined />}
                                    onClick={handleStop}
                                    loading={disconnectLoading}
                                    disabled={disconnectLoading}
                                >
                                    停止连接
                                </Button>
                            )}



                        </div>
                    </Card>
                </Col>

                {/* 交互区域 */}
                <Col lg={10} xs={24}>
                    <Card>
                        <Tabs defaultActiveKey="1">
                            <TabPane tab="对话框" key="1">
                                <div className={styles.messageBox}>
                                    {messages.map((msg, index) => (
                                        <div
                                            key={index}
                                            className={`${styles.message} ${msg.sender === 'user' ? styles.userMessage : styles.systemMessage}`}
                                        >
                                            <strong>
                                                {msg.sender === 'user' ? '您' :
                                                    msg.sender === 'system' ? '系统' : '数字人'}:
                                            </strong> {msg.text}
                                        </div>
                                    ))}
                                </div>

                                <TextArea
                                    rows={5}
                                    placeholder="输入您想对数字人说的话..."
                                    value={chatInput}
                                    onChange={(e) => setChatInput(e.target.value)}
                                    onPressEnter={handleSendChatMessage}
                                    className={styles.chatInput}
                                />

                                <Button
                                    type="primary"
                                    icon={<SendOutlined />}
                                    onClick={handleSendChatMessage}
                                    disabled={status !== 'connected' || !chatInput.trim()}
                                    block
                                    className={styles.sendButton}
                                >
                                    发送
                                </Button>

                                <div className={styles.voiceControl}>
                                    <div
                                        onMouseDown={handleVoiceStart}
                                        onMouseUp={handleVoiceEnd}
                                        onMouseLeave={handleVoiceEnd}
                                        onTouchStart={handleVoiceStart}
                                        onTouchEnd={handleVoiceEnd}
                                        className={`${styles.voiceButton} ${isListening ? styles.listening : ''}`}
                                        // 修复：内联样式传递动态值
                                        style={{
                                            opacity: status === 'connected' ? 1 : 0.5,
                                            transform: isListening ? 'scale(1.1)' : 'scale(1)',
                                            backgroundColor: isListening ? '#f5222d' : '#1890ff',
                                            cursor: status === 'connected' ? 'pointer' : 'not-allowed',
                                            boxShadow: isListening
                                                ? '0 0 0 10px rgba(245, 34, 45, 0.3)'
                                                : '0 2px 5px rgba(0,0,0,0.2)'
                                        }}
                                    >
                                        <AudioOutlined className={styles.voiceIcon} />
                                    </div>
                                    <div className={styles.voiceTip}>
                                        按住说话，松开发送
                                    </div>
                                    {isListening && (
                                        <div className={styles.listeningStatus}>
                                            正在录音...
                                        </div>
                                    )}
                                </div>
                            </TabPane>
                            {/*
                            <TabPane tab="朗读模式" key="2">
                                <TextArea
                                    rows={6}
                                    placeholder="输入您想让数字人朗读的文字..."
                                    value={ttsInput}
                                    onChange={(e) => setTtsInput(e.target.value)}
                                    className={styles.ttsInput}
                                />
                                <Button
                                    type="primary"
                                    icon={<SoundOutlined />}
                                    onClick={handleSendTTSMessage}
                                    disabled={status !== 'connected' || !ttsInput.trim()}
                                    block
                                >
                                    朗读文本
                                </Button>
                            </TabPane>
                            */}
                        </Tabs>
                    </Card>
                </Col>
            </Row>


        </div>
    );
};

export default ProfessionChat;